import type { Tool, ToolPatchPayload } from "@repo/app-types/entities/tool";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import type { FC } from "react";
import { useNavigate, useParams } from "react-router";
import {
  useGetToolQuery,
  useUpdateToolMutation,
} from "../api/entities/tool.js";
import { getApiErrorMessage } from "../api/helpers/errors.js";
import { appPaths } from "../app/appPaths.js";
import { PageBody, PageLayout } from "../components/layout/pageLayout.js";
import {
  NotificationType,
  showNotification,
} from "../features/notificationsOverlay/notificationsSlice.js";
import { ToolFormActions } from "../features/tools/components/toolFormActions.js";
import { useToolForm } from "../features/tools/hooks/useToolForm.js";
import { ToolMoreMenu } from "../features/tools/toolMoreMenu.js";
import { useAppDispatch } from "../hooks/hook.js";

export const ToolDetailsPage: FC = () => {
  const { toolId } = useParams();
  if (!toolId) {
    throw new Error("Missing tool ID");
  }

  const navigate = useNavigate();
  const { data: tool, isLoading, isError } = useGetToolQuery(toolId);

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Wide}
        title={tool?.name ?? "Tool"}
        isLoading={isLoading}
        actions={
          tool && (
            <ToolMoreMenu
              tool={tool}
              toolId={toolId}
              onToolDeleted={() => navigate(appPaths.agentTools.build())}
            />
          )
        }
      />
      <PageBody width={PageWidth.Wide}>
        {isError && <div>Failed to load tool.</div>}
        {tool ? <ToolConfiguration tool={tool} /> : null}
      </PageBody>
    </PageLayout>
  );
};

const ToolConfiguration: FC<{ tool: Tool }> = ({ tool }) => {
  const dispatch = useAppDispatch();

  const [updateTool] = useUpdateToolMutation();

  const handleSave = async (payload: ToolPatchPayload) => {
    try {
      await updateTool({
        toolId: tool.id,
        payload,
      }).unwrap();

      showNotification(dispatch, {
        id: `tool-update-${new Date().getTime().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Tool updated",
        secondaryMessage: "Tool has been updated successfully.",
      });
    } catch (error) {
      console.error("Failed to update tool:", error);
      showNotification(dispatch, {
        id: `tool-update-error-${new Date().getTime().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to update tool",
        secondaryMessage: getApiErrorMessage(error, "Please try again."),
      });

      // Rethrow so the form keeps the user's changes
      throw error;
    }
  };

  // No-op for edit page - the forms handle discard internally
  const handleCancel = () => {};

  const {
    isEditMode,
    isFormValid,
    hasChanges,
    isSaving,
    onSave,
    onDiscard,
    renderForm,
  } = useToolForm({
    type: tool.type,
    initialData: tool,
    onSave: handleSave,
  });

  return (
    <div className="w-full rounded-lg border border-border-light bg-white">
      <div className="border-b border-border-light p-4">
        <Label size={Size.MEDIUM}>Tool settings</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          Configure the settings for this tool. To manage its usage within an
          agent, go to the agent&apos;s settings. To provide guidance for when
          the tool should be used, add context to your Playbook.
        </p>
      </div>

      <div className="flex w-full flex-col space-y-3 p-4">
        {renderForm()}

        <ToolFormActions
          isEditMode={isEditMode}
          hasChanges={hasChanges}
          isFormValid={isFormValid}
          isSaving={isSaving}
          onSave={onSave}
          onDiscard={onDiscard}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
};
